<template>
  <div
    v-show="gridVisible"
    class="grid-wrapper gridScroll"
    style="height: 100%"
  >
    <ag-grid-vue
      v-loading="loading"
      style="height: 100%"
      class="ag-theme-balham"
      :grid-options="gridOptions"
      :column-defs="getColumns"
      :row-data="rows"
      :get-row-style="getRowStyle"
      :get-row-class="getRowClass"
      :row-selection="rowSelection"
      :default-col-def="defaultColDef"
      :overlay-loading-template="overlayLoadingTemplate"
      :framework-components="frameworkComponents"
      :context="context"
      :suppress-cell-focus="suppressCellFocus"
      :suppress-row-transform="suppressRowTransform"
      :get-row-height="getRowHeight"
      :stop-editing-when-cells-lose-focus="true"
      :enable-browser-tooltips="true"
      :tooltip-hide-delay="tooltipHideDelay"
      :tooltip-show-delay="tooltipShowDelay"
      :suppress-row-click-selection="suppressRowClickSelection"
      :pinned-top-row-data="pinnedTopRowData"
      :pinned-bottom-row-data="pinnedBottomRowData"
      :suppress-row-hover-highlight="suppressRowHoverHighlight"
      :row-class-rules="rowClassRules"
      :is-external-filter-present="isExternalFilterPresent"
      :does-external-filter-pass="doesExternalFilterPass"
      :is-row-selectable="isRowSelectable"
      :row-multi-select-with-click="rowMultiSelectWithClick"
      :is-full-width-row="isFullWidthRow"
      :full-width-cell-renderer="fullWidthCellRendererComp"
      :embed-full-width-rows="embedFullWidthRows"
      @sortChanged="onSortChanged"
      @columnResized="columnResized"
      @selection-changed="onSelectionChanged"
      @rowClicked="onRowClicked"
      @cellClicked="onCellClicked"
      @first-data-rendered="onFirstDataRendered"
      @cellValueChanged="rowValueChanged"
      @cellContextMenu="onCellContextMenu"
    />
  </div>
</template>
<script>
import FullWidthCellRenderer from './fullWidthCellRenderer.js'
import { AgGridVue } from 'ag-grid-vue'
import {
  DEFAULT_GRID_ROW_HEIGHT,
  DEFAULT_GRID_HEADER_HEIGHT,
  DEFAULT_GRID_COL_DEF
} from '@/constant'
import { mapState } from 'vuex'

export default {
  components: {
    AgGridVue,
    // eslint-disable-next-line vue/no-unused-components
    FullWidthCellRenderer
  },
  props: {
    suppressRowHoverHighlight: {
      type: Boolean
    },
    getRowStyle: {
      type: Function
    },
    getRowClass: {
      type: Function
    },
    columns: {
      type: Array,
      default: () => []
    },
    rows: {
      type: Array,
      default: () => []
    },
    disabledSortable: {
      type: Boolean,
      default: false
    },
    frameworkComponents: {
      type: Object,
      default: () => null
    },
    context: {
      type: Object,
      default: () => {}
    },
    suppressCellFocus: {
      type: Boolean,
      default: false
    },
    suppressRowTransform: {
      type: Boolean,
      default: false
    },
    rowSelection: {
      type: String,
      default: 'multiple'
    },
    pagination: {
      type: Boolean,
      default: false
    },
    defaultColDef: {
      type: Object,
      default: () => {
        return DEFAULT_GRID_COL_DEF
      }
    },
    treeData: {
      type: Boolean,
      default: false
    },
    autoGroupColumnDef: {
      type: Object,
      default: () => null
    },
    groupDefaultExpanded: {
      type: Number,
      default: -1
    },
    groupField: {
      type: String,
      default: ''
    },
    pinnedTopRowData: {
      type: Array,
      default: null
    },
    pinnedBottomRowData: {
      type: Array,
      default: null
    },
    headerHeight: {
      type: Number,
      default: DEFAULT_GRID_HEADER_HEIGHT
    },
    breakPoint: {
      type: Number,
      default: 1200
    },
    domLayout: {
      type: String,
      default: 'autoHeight'
    },
    suppressRowClickSelection: {
      type: Boolean,
      default: false
    },
    rowHeight: {
      default: 80
    },
    rowClassRules: null,
    reRenderRow: {
      default: false
    },
    autoSizeCell: {
      type: Boolean,
      default: false
    },
    autoSelectFirstRow: {
      type: Boolean,
      default: false
    },
    isExternalFilterPresent: null,
    doesExternalFilterPass: null,
    isRowSelectable: {
      type: Function,
      default: () => true
    },
    rowMultiSelectWithClick: {
      type: Boolean,
      default: false
    },
    fullWidthCellRenderer: null,
    embedFullWidthRows: {
      type: Boolean,
      default: false
    },
    headerHeightSetter: {
      type: Function,
      default: () => {}
    },
    isHeaderHeightSetter: {
      type: Boolean,
      default: false
    },
    wantToGetRowSelectedEvent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      gridVisible: false,
      sortColumn: null,
      sortOrder: null, // 1:Ascending, 2:Descending
      gridOptions: null,
      gridApi: null,
      overlayLoadingTemplate:
        '<span class="ag-overlay-loading-center"><div class="loader">Loading...</div></span>',
      selectedData: null,
      getDataPath: null,
      scrollUse: false,
      // rowHeight: 38,

      tooltipShowDelay: 0,
      tooltipHideDelay: 0
    }
  },
  computed: {
    ...mapState({
      sidebar: state => state.app.sidebar
    }),
    getColumns() {
      const disabled = this.disabledSortable
      if (disabled === null) {
        return this.columns
      } else {
        return this.columns.map(c => ({
          ...c,
          comparator: () => {
            return 0
          }
        }))
      }
    },
    getSideState() {
      return this.$store.getters['ui/getSideState']
    },
    getTab() {
      return this.$store.getters['tab']
    }
  },
  watch: {
    'sidebar.opened': {
      handler(nv, ov) {
        this.deviceScrollCheck()
        // this.sizeToFit()
        // this.gridApi.sizeColumnsToFit()
      },
      immediate: true
    },
    rows() {
      setTimeout(() => {
        this.deviceScrollCheck()
        // Auto select first Row
        if (this.autoSelectFirstRow) {
          this.gridOptions.api.forEachNode(function (node) {
            node.setSelected(node.rowIndex === 0)
          })
        }
      })
    },
    selectedData() {
      this.$emit('changeSelectedData', this.selectedData)
    },
    reRenderRow() {
      if (this.reRenderRow) {
        this.refreshAll()
      }
    },
    getSideState() {
      this.loading = true
      setTimeout(() => {
        this.sizeToFit()
        this.loading = false
      }, 500)
    },
    getTab() {
      const gridApis = [this.gridApi]
      gridApis.forEach(item => {
        item.sizeColumnsToFit()
      })
    }
  },
  created() {
    if (this.treeData) {
      this.getDataPath = data => {
        return data[this.groupField]
      }
    }
    // this.sizeToFit()
    // setTimeout(this.sizeToFit)
    window.addEventListener('resize', this.deviceScrollCheck)
    setTimeout(() => {
      this.gridVisible = true
    })
  },
  beforeMount() {
    this.gridOptions = {
      // deltaRowDataMode: true,
      // getRowNodeId: (data) => {
      //   return data.id
      // },
      // suppressScrollOnNewData: true,
      suppressCellSelection: true,
      pagination: false,
      paginationAutoPageSize: false,
      domLayout: this.domLayout,
      headerHeight: this.headerHeight,
      // rowHeight: this.rowHeight,
      localeText: { noRowsToShow: 'Không có dữ liệu.' },
      suppressContextMenu: true,
      onRowDoubleClicked: this.onDoubleClick,
      rowBuffer: 300
      // isFullWidthRow: params => {
      //   if (params.rowNode.data.isFullWidthRow) {
      //     return true
      //   } else {
      //     return false
      //   }
      // },
      // fullWidthCellRenderer: FullWidthCellRenderer

      // getRowId: params => params.data.id,
      // wrapHeaderText: true,
    }

    this.isFullWidthRow = params => {
      if (params.rowNode.data?.isFullWidthRow) {
        return true
      } else {
        return false
      }
    }
    if (!this.fullWidthCellRenderer) {
      this.fullWidthCellRendererComp = 'FullWidthCellRenderer'
    } else {
      this.fullWidthCellRendererComp = this.fullWidthCellRenderer
    }
  },
  mounted() {
    this.loading = true
    this.gridApi = this.gridOptions.api
    this.columnApi = this.gridOptions.columnApi
    const gridApis = [this.gridApi]
    this.$store.commit('ui/setGridApi', gridApis)
    setTimeout(() => {
      const scrollEle = document.querySelector('.ag-body-horizontal-scroll')
      if (scrollEle.classList.contains('ag-hidden')) {
        // this.gridApi.sizeColumnsToFit()
      } else {
        // do nothing
      }
      this.loading = false
      // check bookingIs is present? (hard code)
      const urlParams = new URLSearchParams(window.location.search)
      const bookingId = urlParams.get('booking_id')
      this.gridOptions.api.forEachNode(function (node) {
        if (bookingId) {
          // eslint-disable-next-line eqeqeq
          node.setSelected(node.data.id == bookingId)
        }
        // }
      })
    }, 500)
    this.sizeToFit()
  },
  beforeDestroy() {},
  methods: {
    onCellContextMenu(event) {
      this.$emit('cellContextMenu', event)
    },
    onRowClick(event) {
      if (event.targetType === 'cell') {
        const { rowKey } = event
        rowKey
        this.$emit('rowClick', this.rows[rowKey])
      }
    },
    onSortChanged(e) {
      this.sortColumn = null
      this.sortOrder = null
      e.columnApi.getColumnState().forEach(c => {
        if (c.sort !== null) {
          this.sortColumn = c.colId
          // this.sortOrder = c.sort === 'asc' ? 1 : 2;
          this.sortOrder = c.sort
          return false
        }
      })
      this.doRefresh()
    },
    doRefresh() {
      const params = {
        orderBy: this.sortColumn,
        sort: this.sortOrder
      }
      this.$emit('refresh', params)
    },
    camelToSnakeCase(field) {
      return field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
    },
    onSelectionChanged() {
      const selectedRows = this.gridApi.getSelectedRows()
      this.$emit('selectionChanged', selectedRows)
    },
    onRowClicked(event) {
      const selectedRows = this.gridApi.getSelectedRows()
      // console.log(event)
      if (this.wantToGetRowSelectedEvent) {
        this.$emit('rowClicked', event)
      } else {
        this.$emit('rowClicked', selectedRows)
      }
    },
    columnResized(event) {
      if (this.isHeaderHeightSetter) {
        this.headerHeightSetter()
      }
      // console.log('Column Resized')
      this.$emit('columnResized', event)
    },
    onCellClicked(params) {
      // console.log('cell clicked: ', params.column.colId);
    },
    getRowSelected() {
      const selectedNodes = this.gridApi.getSelectedNodes()
      this.selectedData = selectedNodes.map(node => node.data)
      return this.selectedData
    },
    onDoubleClick(event) {
      const selectedRows = this.gridApi.getSelectedRows()
      this.$emit('doubleClicked', selectedRows, event)
    },
    // eslint-disable-next-line no-unused-vars
    onFirstDataRendered(params) {
      if (this.isHeaderHeightSetter) {
        this.headerHeightSetter()
      }
      this.deviceScrollCheck()
    },
    sizeToFit() {
      setTimeout(() => {
        const scrollEle = document.querySelector('.ag-body-horizontal-scroll')
        if (scrollEle.classList.contains('ag-hidden')) {
          // this.gridApi.sizeColumnsToFit()
        } else {
          // do nothing
        }

        // const gridBodyWidth = document.getElementsByClassName('ag-root-wrapper')[0].clientWidth // todo: use grid's id instead of [0] to allow multiple grids on a page
        // const allColumnsWidth = columnApi.getAllDisplayedColumns().map(c => c.getActualWidth()).reduce((a, b) => a + b, 0)
        // if (gridBodyWidth > allColumnsWidth) {
        //   gridApi.sizeColumnsToFit()
        // }
        if (this.autoSizeCell) {
          this.gridApi.sizeColumnsToFit()
        }
      }, 300)
      // if (window.innerWidth > 1300) {
      //   console.log(12345)
      //   this.gridApi.sizeColumnsToFit()
      // }
    },
    autoSizeAll() {
      const allColumnIds = []
      if (this.gridOptions?.columnApi) {
        this.gridOptions.columnApi.getColumns().forEach(column => {
          allColumnIds.push(column.getId())
        })
        this.gridOptions.columnApi.autoSizeColumns(allColumnIds, false)
      }
    },
    deviceScrollCheck() {
      this.sizeToFit()
      // this.gridApi.sizeColumnsToFit()
      // this.autoSizeAll()
      // console.log('deviceScrollCheck')
      // this.sizeToFit()
      // if (this.autoSizeCell) {
      //   this.scrollUse = true
      //   return
      // }
      // if (window.innerWidth <= this.breakPoint) {
      //   this.autoSizeAll()
      //   this.scrollUse = true
      //   console.log('auto size all')
      // } else {
      //   this.scrollUse = false
      // }
    },
    getRowHeight(params) {
      // console.log('getRowHeight: ', params.data)
      // if(params.data.)
      if (params.data?.rowHeightTotal) {
        return 50
      }
      let length = 1
      for (const property in params.data) {
        const stringItem = String(params.data[property])
        if (params.data[property] && stringItem.includes('<br/>')) {
          if (length < stringItem.split('<br/>').length) {
            length = stringItem.split('<br/>').length
          }
        }
      }
      const height = DEFAULT_GRID_ROW_HEIGHT * length
      return height
    },
    setGroupHeight(height) {
      // eslint-disable-next-line
      this.rowHeight = height
      this.gridApi.resetRowHeights()
    },
    rowValueChanged() {
      const selectedRows = this.gridApi.getSelectedRows()
      this.$emit('rowValueChanged', selectedRows)
    },
    refreshAll() {
      this.gridApi.redrawRows()
      // this.gridApi.refreshRows();
    }
  }
}
</script>
